import { Dispatch } from "@reduxjs/toolkit";
import { message } from "antd";
import { RootState } from "../../../app/store";
import { decideResponse, myfetchThunk } from "../../../app/myfetch";
import { masteruri } from "../../../config";
import { actions } from "../actions/Action";
import { AuthSecrets, AuthSettings, ConnectionKind } from "../typing/generalType";
import { parseAuthSettings, parseErrors, parseTestResult, toPutBody } from "../typing/authSettingsLogic";

const settingsUri = () => masteruri + "/AuthSettings";

export const requestAuthSettingsFromAPI = () => {
    return (dispatch: Dispatch<any>, _getState: () => RootState) => {
        dispatch(myfetchThunk(
            true,
            settingsUri(),
            {},
            "GET",
            (payload: unknown) => dispatch(actions.settingsLoaded({ settings: parseAuthSettings(payload) })),
            () => dispatch(actions.loadFailed({})),
        ) as any);
    };
};

/** What a PUT answer means for the page. */
type SaveOutcome =
    | { kind: "saved" }
    | { kind: "refused"; errors: readonly string[] }
    | { kind: "failed"; reason: string };

/**
 * Judges the answer to PUT. Separate from the request so the 400 body is read
 * rather than dropped: myfetchThunk hands the error callback only the status,
 * and it cannot take a 200 without a body either - the backend may answer the
 * save with nothing.
 */
function decideSave(status: number, body: unknown): SaveOutcome {
    const outcome = decideResponse(status, false);
    if (outcome.kind === "body") return { kind: "saved" };
    if (status === 400) {
        const errors = parseErrors(body);
        return { kind: "refused", errors: errors.length > 0 ? errors : ["HTTP 400"] };
    }
    return { kind: "failed", reason: outcome.kind === "failed" ? outcome.reason : "HTTP " + status };
}

/** Reads a body that may be empty or not JSON; either way the result is a value. */
const readBody = (response: Response): Promise<unknown> =>
    response.text().then((text) => {
        try {
            return text === "" ? {} : JSON.parse(text);
        } catch {
            return {};
        }
    });

export const saveAuthSettingsToAPI = (settings: AuthSettings, secrets: AuthSecrets, savedText: string) => {
    return (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(actions.saveStarted({}));

        fetch(settingsUri(), {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + getState().LoginManager.model.token,
            },
            body: JSON.stringify(toPutBody(settings, secrets)),
        })
            .then((response) => readBody(response).then((body) => decideSave(response.status, body)))
            .catch((): SaveOutcome => decideSave(0, {}))
            .then((outcome) => {
                if (outcome.kind === "saved") {
                    message.success(savedText);
                    // Read back what the hub stored, including the *Set flags.
                    dispatch(requestAuthSettingsFromAPI() as any);
                } else if (outcome.kind === "refused") {
                    dispatch(actions.saveRefused({ errors: outcome.errors }));
                } else {
                    message.error(outcome.reason);
                    dispatch(actions.saveRefused({ errors: [outcome.reason] }));
                }
            });
    };
};

/** Tests the settings as stored on the hub, not what is typed into the page. */
export const testConnectionOnAPI = (kind: ConnectionKind) => {
    return (dispatch: Dispatch<any>, _getState: () => RootState) => {
        dispatch(actions.setTestResult({ kind, result: { kind: "running" } }));
        dispatch(myfetchThunk(
            true,
            settingsUri() + "/test/" + kind,
            {},
            "POST",
            (payload: unknown) => dispatch(actions.setTestResult({ kind, result: parseTestResult(payload) })),
            (problem: { reason?: string }) => dispatch(actions.setTestResult({
                kind,
                result: { kind: "done", ok: false, message: problem?.reason ?? "" },
            })),
        ) as any);
    };
};
